import React, { useEffect, useRef } from "react";
import { useMap, MapControl, ControlPosition } from "@vis.gl/react-google-maps";
import { Box, Typography } from "@mui/material";

import { MapDrawMode, MapRingPoint } from "./types";

/**
 * ── DRAWING AN AREA ON THE MAP (VRG-480) ────────────────────────────────────────────
 *
 * Renders the committed ring, and — while `mode` is not `"off"` — captures a new one.
 * A layer-1 (`@repo/ui`) capability rather than a platform-local one because it is a
 * property of a MAP, not of the analytics page: any surface plotting points may
 * reasonably want a region selected on it (§47 — improve the shared component rather
 * than building around it).
 *
 * ## Why not `google.maps.drawing.DrawingManager`
 *
 * The drawing library was the obvious first move and it does not do the thing the spec
 * asks for. `DrawingManager` offers `polygon`, `polyline`, `rectangle` and `circle`, all
 * of them CLICK-to-place-vertices; it has no freehand mode at all. Freehand is the
 * default here, so the library would cover the secondary case only — while costing an
 * extra `libraries={['drawing']}` load on every map in both apps, including the ones that
 * never draw anything. The gesture below is ~80 lines against Google's own map events,
 * with no additional bundle.
 *
 * ## The gesture, and the two things that make it work
 *
 * FREEHAND: `mousedown` starts, `mousemove` accumulates, `mouseup` closes. Two details
 * are load-bearing and neither is obvious:
 *
 *  1. **`draggable: false` for the duration.** A map's default `mousedown` + drag IS a
 *     pan. Without suppressing it the map slides under the stroke and the ring is drawn
 *     in coordinates that moved while it was being drawn — a shape the user never saw.
 *     Restored on teardown, always, including when the component unmounts mid-stroke.
 *  2. **A `window` `mouseup` fallback.** Releasing the button outside the map canvas —
 *     which is easy, the map is 600px in a scrolling page — never fires the map's own
 *     `mouseup`, so the stroke would stay armed and the next `mousemove` over the map
 *     would keep extending a ring the user believed finished.
 *
 * POLYGON: click places a vertex, double-click closes. `disableDoubleClickZoom` is set
 * while armed, because otherwise the closing gesture also zooms the map two steps.
 *
 * ## Escape cancels
 *
 * Bound at the window, not the map, because the map does not hold keyboard focus during a
 * mouse gesture. Cancelling clears the in-progress stroke without emitting, which is the
 * only way out of a half-drawn polygon that does not commit an area nobody wanted.
 *
 * ## Discoverability (VERA-260) — "how do I finish this?"
 *
 * A polygon could always be closed by double-click OR Enter, and cancelled with Escape,
 * and NONE of the three was discoverable — nothing on screen said any of it. Two
 * additions, deliberately not one:
 *
 *  1. **Snap-to-close** — as the cursor nears the FIRST vertex (`SNAP_PX`, screen space),
 *     that vertex visibly grows and takes the draw colour, and a click there closes the
 *     ring. This is the load-bearing half: it is the universal convention (Google's own
 *     drawing tools, Figma's pen tool) and it is self-teaching — the feedback IS the
 *     instruction, so it works for someone who never reads a hint.
 *  2. **A hint**, top-centre of the map, naming all three ways out — reinforcement, not
 *     the fix, and rendered ONLY while a polygon gesture is armed (`mode === 'polygon'`);
 *     permanent text would be clutter on every other map.
 *
 * Freehand keeps neither: its "first point" is just the start of the stroke, not a target
 * you return to (it closes on mouseup), so a snap target there would invite a click that
 * does nothing.
 */

/** Minimum vertices for a ring to enclose anything. Two points are a line segment. */
const MIN_RING_POINTS = 3;

/** Freehand sampling floor, in SCREEN pixels — see the note at its use. */
const MIN_SAMPLE_PX = 3;

/**
 * Snap-to-close radius, in SCREEN pixels — see the file header. ~10-12px is the
 * conventional target size for a small on-map affordance (large enough to hit
 * deliberately, small enough not to swallow clicks meant to place a nearby vertex).
 */
const SNAP_PX = 12;

/**
 * Screen-pixel distance between two points at the map's CURRENT zoom, via the map's own
 * projection. The freehand sampler below and the polygon snap-to-close check both need
 * "screen pixels", not degrees — a degree threshold means something different at every
 * zoom level, whereas this conversion means the same thing everywhere. Factored out so
 * the two call sites cannot drift apart on the maths.
 */
const pixelDistance = (
  map: google.maps.Map,
  a: MapRingPoint,
  b: MapRingPoint,
): number | null => {
  const projection = map.getProjection();
  if (!projection) return null;
  const scale = 2 ** (map.getZoom() ?? 0);
  const pa = projection.fromLatLngToPoint(new google.maps.LatLng(a));
  const pb = projection.fromLatLngToPoint(new google.maps.LatLng(b));
  if (!pa || !pb) return null;
  return Math.hypot((pb.x - pa.x) * scale, (pb.y - pa.y) * scale);
};

/**
 * The first-vertex marker's icon — a small ring that fills solid and grows to
 * `near`, which IS the "click here to close" instruction (see the file header on why
 * this, not the hint text, is the primary fix).
 */
const firstVertexIcon = (color: string, near: boolean): google.maps.Icon => {
  const r = near ? 8 : 5;
  const size = r * 2 + 8;
  const c = size / 2;
  return {
    url:
      "data:image/svg+xml;charset=UTF-8," +
      encodeURIComponent(
        `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">` +
          `<circle cx="${c}" cy="${c}" r="${r}" fill="${near ? color : "#FFFFFF"}" stroke="${color}" stroke-width="2" />` +
          `</svg>`,
      ),
    anchor: new google.maps.Point(c, c),
    scaledSize: new google.maps.Size(size, size),
  };
};

export interface MapDrawLayerProps {
  mode: MapDrawMode;
  /** The committed ring, drawn as a filled polygon. */
  ring?: MapRingPoint[] | null;
  /** Fires once, with the raw gesture. The consumer simplifies and caps it. */
  onDrawComplete?: (ring: MapRingPoint[]) => void;
  /** Fires when the user abandons a stroke (Escape, or a gesture too small to enclose). */
  onDrawCancel?: () => void;
  /** Stroke/fill colour. Defaults to the brand purple the pins use. */
  color?: string;
}

export const MapDrawLayer: React.FC<MapDrawLayerProps> = ({
  mode,
  ring,
  onDrawComplete,
  onDrawCancel,
  color = "#7F48DF",
}) => {
  const map = useMap();

  // The committed shape, and the in-progress one, as separate overlays: the committed
  // polygon must survive re-arming (you can draw a new area while the old one is shown),
  // and the live trace is torn down on every stroke.
  const committedRef = useRef<google.maps.Polygon | null>(null);
  const liveRef = useRef<google.maps.Polyline | null>(null);
  const pointsRef = useRef<MapRingPoint[]>([]);
  const drawingRef = useRef(false);
  // Snap-to-close (polygon only) — the first vertex's marker, and whether the cursor is
  // currently within `SNAP_PX` of it. See the file header.
  const firstVertexMarkerRef = useRef<google.maps.Marker | null>(null);
  const nearFirstRef = useRef(false);
  // Latest callbacks without re-binding the listeners on every parent render — a
  // re-bind mid-stroke would drop the gesture.
  const completeRef = useRef(onDrawComplete);
  const cancelRef = useRef(onDrawCancel);
  completeRef.current = onDrawComplete;
  cancelRef.current = onDrawCancel;

  /* ── The committed ring ─────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!map) return;

    committedRef.current?.setMap(null);
    committedRef.current = null;

    if (ring && ring.length >= MIN_RING_POINTS) {
      committedRef.current = new google.maps.Polygon({
        paths: ring,
        map,
        strokeColor: color,
        strokeOpacity: 0.9,
        strokeWeight: 2,
        fillColor: color,
        // Light enough that the pins inside stay legible — the shape is a boundary, and
        // the points it contains are the answer. A heavier fill hides the very thing the
        // selection was made to look at.
        fillOpacity: 0.1,
        // Non-interactive: the polygon covers the pins, so a clickable one would swallow
        // every marker click inside the area the user just selected.
        clickable: false,
        zIndex: 1,
      });
    }

    return () => {
      committedRef.current?.setMap(null);
      committedRef.current = null;
    };
  }, [map, ring, color]);

  /* ── Capture ────────────────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!map || mode === "off") return;

    const previousDraggable = map.get("draggable") !== false;
    const previousCursor = map.get("draggableCursor");

    map.setOptions({
      draggable: mode !== "freehand",
      draggableCursor: "crosshair",
      disableDoubleClickZoom: true,
    });

    const clearLive = () => {
      liveRef.current?.setMap(null);
      liveRef.current = null;
      firstVertexMarkerRef.current?.setMap(null);
      firstVertexMarkerRef.current = null;
      nearFirstRef.current = false;
      pointsRef.current = [];
      drawingRef.current = false;
    };

    const ensureLive = () => {
      if (liveRef.current) return liveRef.current;
      liveRef.current = new google.maps.Polyline({
        map,
        path: [],
        strokeColor: color,
        strokeOpacity: 0.95,
        strokeWeight: 2,
        clickable: false,
        zIndex: 2,
      });
      return liveRef.current;
    };

    // Snap-to-close vertex marker (polygon only) — created the moment the first point is
    // placed, restyled by the mousemove proximity check below. Non-interactive
    // (`clickable: false`): closing happens via the map's own `click`, not a click on this
    // marker, so it never competes with that listener for the event.
    const updateFirstVertexMarker = (point: MapRingPoint, near: boolean) => {
      if (!firstVertexMarkerRef.current) {
        firstVertexMarkerRef.current = new google.maps.Marker({
          position: point,
          map,
          clickable: false,
          zIndex: 3,
          icon: firstVertexIcon(color, near),
        });
      } else {
        firstVertexMarkerRef.current.setIcon(firstVertexIcon(color, near));
      }
    };

    const push = (latLng: google.maps.LatLng | null) => {
      if (!latLng) return;
      const point = { lat: latLng.lat(), lng: latLng.lng() };
      const previous = pointsRef.current[pointsRef.current.length - 1];
      if (previous) {
        // Sample at a floor of a few SCREEN pixels rather than a fixed number of degrees.
        // A degree threshold is meaningless across zoom levels — at world zoom it would
        // reject the whole gesture, at street zoom it would reject nothing — whereas the
        // projection makes the pixel floor mean the same thing everywhere. It exists so a
        // slow, deliberate stroke does not emit one vertex per animation frame; the
        // simplifier still does the real reduction.
        const dist = pixelDistance(map, previous, point);
        if (dist !== null && dist < MIN_SAMPLE_PX) return;
      }
      pointsRef.current = [...pointsRef.current, point];
      ensureLive().setPath(pointsRef.current);
      if (mode === "polygon" && pointsRef.current.length === 1) {
        updateFirstVertexMarker(point, false);
      }
    };

    const finish = () => {
      const points = pointsRef.current;
      clearLive();
      if (points.length >= MIN_RING_POINTS) {
        completeRef.current?.(points);
      } else {
        // A click or a flick. Not an area, and reporting it as one would apply a filter
        // matching nothing — see `prepareRing`, which refuses the same shape.
        cancelRef.current?.();
      }
    };

    const listeners: google.maps.MapsEventListener[] = [];

    if (mode === "freehand") {
      listeners.push(
        map.addListener("mousedown", (event: google.maps.MapMouseEvent) => {
          drawingRef.current = true;
          pointsRef.current = [];
          push(event.latLng ?? null);
        }),
        map.addListener("mousemove", (event: google.maps.MapMouseEvent) => {
          if (!drawingRef.current) return;
          push(event.latLng ?? null);
        }),
        map.addListener("mouseup", () => {
          if (!drawingRef.current) return;
          finish();
        }),
      );
    } else {
      listeners.push(
        map.addListener("click", (event: google.maps.MapMouseEvent) => {
          // Snap-to-close: a click while the cursor is near the first vertex finishes
          // the ring AT the already-placed points, rather than placing a new (near-
          // duplicate) vertex at the click position — Google's Polygon auto-closes the
          // path back to its first point, so nothing further needs adding.
          if (drawingRef.current && nearFirstRef.current && pointsRef.current.length >= MIN_RING_POINTS) {
            finish();
            return;
          }
          drawingRef.current = true;
          push(event.latLng ?? null);
        }),
        map.addListener("dblclick", (event: google.maps.MapMouseEvent) => {
          if (!drawingRef.current) return;
          push(event.latLng ?? null);
          finish();
        }),
        // Proximity check for the snap target — see the file header + `firstVertexIcon`.
        // Only meaningful once there are enough placed vertices that closing NOW would
        // enclose something (MIN_RING_POINTS); before that, closing at the first vertex
        // would be a degenerate 1-2 point "ring" `finish()` already refuses.
        map.addListener("mousemove", (event: google.maps.MapMouseEvent) => {
          if (!drawingRef.current) return;
          const first = pointsRef.current[0];
          const latLng = event.latLng;
          if (!first || !latLng || pointsRef.current.length < MIN_RING_POINTS) {
            if (nearFirstRef.current) {
              nearFirstRef.current = false;
              if (first) updateFirstVertexMarker(first, false);
            }
            return;
          }
          const dist = pixelDistance(map, first, { lat: latLng.lat(), lng: latLng.lng() });
          const near = dist !== null && dist <= SNAP_PX;
          if (near !== nearFirstRef.current) {
            nearFirstRef.current = near;
            updateFirstVertexMarker(first, near);
          }
        }),
      );
    }

    // See the header: a release outside the canvas never reaches the map's own handler,
    // and would leave the stroke armed.
    const onWindowMouseUp = () => {
      if (mode !== "freehand" || !drawingRef.current) return;
      finish();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && drawingRef.current) {
        clearLive();
        cancelRef.current?.();
      }
      // Enter closes a polygon, so a shape can be finished from the keyboard rather than
      // only by a double-click nobody is told about.
      if (event.key === "Enter" && mode === "polygon" && drawingRef.current) {
        finish();
      }
    };

    window.addEventListener("mouseup", onWindowMouseUp);
    window.addEventListener("keydown", onKeyDown);

    return () => {
      listeners.forEach((listener) => listener.remove());
      window.removeEventListener("mouseup", onWindowMouseUp);
      window.removeEventListener("keydown", onKeyDown);
      clearLive();
      // Restore panning unconditionally — including when this unmounts mid-stroke, where
      // leaving `draggable: false` behind would produce a map nobody can move and no
      // control on screen to explain why.
      map.setOptions({
        draggable: previousDraggable,
        draggableCursor: previousCursor ?? null,
        disableDoubleClickZoom: false,
      });
    };
  }, [map, mode, color]);

  // The discoverability hint (see file header) — reinforcement for the snap-to-close
  // affordance above, not the fix. Only while a polygon gesture is ARMED, so it never
  // adds permanent clutter to a map that isn't being drawn on; disappears the moment the
  // consumer takes `mode` back to `'off'` (which `handleDrawComplete`/`handleDrawCancel`
  // already do on completion/cancel).
  if (mode !== "polygon") return null;

  return (
    <MapControl position={ControlPosition.TOP_CENTER}>
      <Box
        // `pointerEvents: 'none'` is load-bearing: this floats directly over the map
        // canvas, and a hint that swallows a click meant for the map (placing a vertex,
        // or the snap-close click) would be worse than no hint at all.
        sx={{
          pointerEvents: "none",
          mt: "12px",
          px: "14px",
          py: "6px",
          borderRadius: "999px",
          backgroundColor: (theme: any) => theme.palette.background.paper,
          border: (theme: any) =>
            `1px solid ${theme.palette.primary?.[100] ?? theme.palette.divider}`,
          boxShadow: (theme: any) => theme.shadows[1],
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", whiteSpace: "nowrap" }}
        >
          Click the first point or press ⏎ to finish · Esc to cancel
        </Typography>
      </Box>
    </MapControl>
  );
};
